import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { scheduleKeys } from './queryKeys';
import { startOfWeek, endOfWeek } from 'date-fns';
import { notifySwapCreated, notifySwapResponse, notifySwapApproved } from '@/services/notifications/triggerNotification';

// Error mapping for swap operations
function mapSwapRpcError(message?: string) {
  const m = (message ?? "").toLowerCase();
  // ✅ FIXED: Handle P0001 errors specifically for stale data
  if (m.includes("swap is no longer open") || m.includes("invalid swap")) {
    return "This swap offer is no longer available. The page will refresh to show current offers.";
  }
  if (m.includes("swap_not_found")) return "That swap request no longer exists.";
  if (m.includes("already_responded")) return "You have already responded to this swap request.";
  if (m.includes("swap_expired")) return "This swap request has expired.";
  if (m.includes("shift_not_available")) return "That shift is no longer available for swapping.";
  if (m.includes("time_conflict")) return "This swap would create a scheduling conflict.";
  if (m.includes("same_shift")) return "You cannot swap a shift with itself.";
  if (m.includes("insufficient_permissions")) return "You don't have permission for this action.";
  if (m.includes("cross_tenant_forbidden")) return "You cannot swap shifts with users from another company.";
  if (m.includes("unauthorized")) return "You need to be signed in to do that.";
  if (m.includes("forbidden")) return "You don't have permission to do that.";
  if (m.includes("not_found")) return "That shift or swap request no longer exists.";
  return "Something went wrong with the swap request.";
}

// Types for mutation arguments
interface CreateSwapArgs {
  requesterShiftId: string;
  candidateData: Array<{
    candidate_profile_id: string;
    candidate_shift_id: string;
  }>;
  message?: string;
}

interface RespondToSwapArgs {
  candidateId: string;
  response: 'accepted' | 'declined';
}

interface CancelSwapArgs {
  swapId: string;
}

interface ApproveSwapArgs {
  swapId: string;
  approved: boolean;
}

/**
 * Hook for creating a new shift swap request
 */
export const useCreateSwap = (currentWeek?: Date) => {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  
  return useMutation({
    mutationFn: async (args: CreateSwapArgs) => {
      console.log('[Frontend] Calling create_shift_swap RPC with:', {
        p_requester_shift_id: args.requesterShiftId,
        p_candidate_data: args.candidateData,
        p_message: args.message || null
      });

      const { data, error } = await supabase.rpc('create_shift_swap', {
        p_requester_shift_id: args.requesterShiftId,
        p_candidate_data: args.candidateData,
        p_message: args.message || null
      });

      if (error) {
        console.error('[Frontend] Error from create_shift_swap RPC:', error);
        throw error;
      }

      console.log('[Frontend] Swap created successfully:', data);
      return data; // swap_id
    },
    
    onSuccess: (swapId, args) => {
      toast.success('Swap request sent successfully');
      
      // Trigger notification for candidates (fire-and-forget)
      if (profile?.id && profile?.company_id) {
        notifySwapCreated({
          companyId: profile.company_id,
          swapId: swapId as string,
          candidateProfileIds: args.candidateData.map(c => c.candidate_profile_id),
          requesterName: profile.first_name ? `${profile.first_name} ${profile.last_name || ''}`.trim() : undefined,
        });
      }
      
      // Invalidate relevant queries
      if (profile?.id) {
        queryClient.invalidateQueries({ 
          queryKey: [...scheduleKeys.all, 'mySwapRequests', profile.id] 
        });
        queryClient.invalidateQueries({ 
          queryKey: [...scheduleKeys.all, 'swapOffersForMe', profile.id] 
        });
        
        if (currentWeek) {
          const weekStartISO = startOfWeek(currentWeek).toISOString();
          const weekEndISO = endOfWeek(currentWeek).toISOString();
          queryClient.invalidateQueries({ 
            queryKey: scheduleKeys.myShifts(profile.id, weekStartISO, weekEndISO) 
          });
        }
      }
    },
    
    onError: (error: any) => {
      console.error('[Frontend] Error creating swap:', error);
      const errorMessage = mapSwapRpcError(error.message);
      toast.error(errorMessage);
    }
  });
};

/**
 * Hook for responding to a swap offer (accept/decline)
 */
export const useRespondToSwap = (currentWeek?: Date) => {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  
  return useMutation({
    mutationFn: async (args: RespondToSwapArgs) => {
      console.log('[Frontend] Calling respond_to_swap RPC with:', {
        p_candidate_id: args.candidateId,
        p_response: args.response
      });

      // First get swap details for notification
      const { data: candidateData } = await supabase
        .from('swap_candidates')
        .select('swap_id, shift_swaps!inner(requester_profile_id, company_id)')
        .eq('id', args.candidateId)
        .single();

      const { data, error } = await supabase.rpc('respond_to_swap', {
        p_candidate_id: args.candidateId,
        p_response: args.response
      });

      if (error) {
        console.error('[Frontend] Error from respond_to_swap RPC:', error);
        throw error;
      }

      console.log('[Frontend] Swap response recorded successfully:', data);
      
      // Return swap info for notification
      return {
        result: data,
        swapInfo: candidateData ? {
          swapId: candidateData.swap_id,
          requesterProfileId: (candidateData.shift_swaps as any)?.requester_profile_id,
          companyId: (candidateData.shift_swaps as any)?.company_id,
        } : null,
      };
    },
    
    onSuccess: (data, args) => {
      const action = args.response === 'accepted' ? 'accepted' : 'declined';
      toast.success(`Swap request ${action} successfully`);
      
      // Trigger notification for swap response (fire-and-forget)
      if (data.swapInfo && profile?.first_name && profile?.id) {
        notifySwapResponse({
          companyId: data.swapInfo.companyId,
          swapId: data.swapInfo.swapId,
          response: args.response,
          requesterProfileId: data.swapInfo.requesterProfileId,
          responderProfileId: profile.id,
          acceptedCandidateName: `${profile.first_name} ${profile.last_name || ''}`.trim(),
        });
      }
      
      // Invalidate relevant queries
      if (profile?.id) {
        // FIXED: Invalidate all mySwapRequests queries (with and without filters)
        queryClient.invalidateQueries({ 
          queryKey: [...scheduleKeys.all, 'mySwapRequests', profile.id] 
        });
        
        // FIXED: Invalidate all swapOffersForMe queries (with and without filters)
        queryClient.invalidateQueries({ 
          queryKey: [...scheduleKeys.all, 'swapOffersForMe', profile.id] 
        });
        
        // Invalidate shifts queries if currentWeek is provided
        if (currentWeek) {
          const weekStartISO = startOfWeek(currentWeek).toISOString();
          const weekEndISO = endOfWeek(currentWeek).toISOString();
          queryClient.invalidateQueries({ 
            queryKey: scheduleKeys.myShifts(profile.id, weekStartISO, weekEndISO) 
          });
        }
        
        // CRITICAL FIX: Invalidate eligibleSwapCandidates cache when swaps are accepted/declined
        queryClient.invalidateQueries({
          queryKey: [...scheduleKeys.all, 'eligibleSwapCandidates']
        });
      }
    },
    
    onError: (error: any) => {
      console.error('[Frontend] Error responding to swap:', error);
      const errorMessage = mapSwapRpcError(error.message);
      toast.error(errorMessage);
      
      // ✅ FIXED: Invalidate cache when P0001 errors occur (stale data)
      if (error.code === 'P0001' || error.message?.includes('swap is no longer open')) {
        console.log('[Frontend] P0001 error detected - invalidating swap caches to refresh stale data');
        if (profile?.id) {
          queryClient.invalidateQueries({ 
            queryKey: [...scheduleKeys.all, 'swapOffersForMe', profile.id] 
          });
          queryClient.invalidateQueries({ 
            queryKey: [...scheduleKeys.all, 'mySwapRequests', profile.id] 
          });
        }
      }
    }
  });
};

/**
 * Hook for cancelling a swap request
 */
export const useCancelSwap = (currentWeek?: Date) => {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  
  return useMutation({
    mutationFn: async (args: CancelSwapArgs) => {
      console.log('[Frontend] Calling cancel_shift_swap RPC with:', {
        p_swap_id: args.swapId
      });

      const { data, error } = await supabase.rpc('cancel_shift_swap', {
        p_swap_id: args.swapId
      });

      if (error) {
        console.error('[Frontend] Error from cancel_shift_swap RPC:', error);
        throw error;
      }

      console.log('[Frontend] Swap cancelled successfully:', data);
      return data;
    },
    
    onSuccess: () => {
      toast.success('Swap request cancelled successfully');
      
      // Invalidate relevant queries
      if (profile?.id) {
        // FIXED: Invalidate all mySwapRequests queries (with and without filters)
        queryClient.invalidateQueries({ 
          queryKey: [...scheduleKeys.all, 'mySwapRequests', profile.id] 
        });
        
        // FIXED: Invalidate all swapOffersForMe queries (with and without filters)
        queryClient.invalidateQueries({ 
          queryKey: [...scheduleKeys.all, 'swapOffersForMe', profile.id] 
        });
        
        // CRITICAL FIX: Invalidate myShifts cache to update UI styling immediately
        if (currentWeek) {
          const weekStartISO = startOfWeek(currentWeek).toISOString();
          const weekEndISO = endOfWeek(currentWeek).toISOString();
          queryClient.invalidateQueries({ 
            queryKey: scheduleKeys.myShifts(profile.id, weekStartISO, weekEndISO) 
          });
        }
      }
    },
    
    onError: (error: any) => {
      console.error('[Frontend] Error cancelling swap:', error);
      const errorMessage = mapSwapRpcError(error.message);
      toast.error(errorMessage);
    }
  });
};

/**
 * Hook for manager approval of swap requests
 */
export const useApproveSwap = (currentWeek?: Date) => {
  const queryClient = useQueryClient();
  const { profile } = useAuth();
  
  return useMutation({
    mutationFn: async (args: ApproveSwapArgs) => {
      console.log('[Frontend] Calling approve_shift_swap RPC with:', {
        p_swap_id: args.swapId,
        p_approved: args.approved
      });

      // First get swap details for notification
      const { data: swapData } = await supabase
        .from('shift_swaps')
        .select(`
          requester_profile_id,
          company_id,
          requester:profiles!shift_swaps_requester_profile_id_fkey(first_name, last_name),
          swap_candidates!inner(candidate_profile_id, status)
        `)
        .eq('id', args.swapId)
        .single();

      const { data, error } = await supabase.rpc('approve_shift_swap', {
        p_swap_id: args.swapId,
        p_approved: args.approved
      });

      if (error) {
        console.error('[Frontend] Error from approve_shift_swap RPC:', error);
        throw error;
      }

      console.log('[Frontend] Swap approval recorded successfully:', data);
      
      // Find accepted candidate
      const acceptedCandidate = (swapData?.swap_candidates as any[])?.find(
        (c: any) => c.status === 'accepted'
      );

      return {
        result: data,
        swapInfo: swapData ? {
          requesterProfileId: swapData.requester_profile_id,
          companyId: swapData.company_id,
          requesterName: (swapData.requester as any)?.first_name 
            ? `${(swapData.requester as any).first_name} ${(swapData.requester as any).last_name || ''}`.trim() 
            : undefined,
          acceptedCandidateProfileId: acceptedCandidate?.candidate_profile_id,
        } : null,
      };
    },
    
    onSuccess: (data, args) => {
      const action = args.approved ? 'approved' : 'denied';
      toast.success(`Swap request ${action} successfully`);
      
      // Trigger notification for swap approval (fire-and-forget)
      if (data.swapInfo) {
        notifySwapApproved({
          companyId: data.swapInfo.companyId,
          swapId: args.swapId,
          approvalStatus: args.approved ? 'approved' : 'rejected',
          requesterProfileId: data.swapInfo.requesterProfileId,
          acceptedCandidateProfileId: data.swapInfo.acceptedCandidateProfileId,
          requesterName: data.swapInfo.requesterName,
        });
      }
      
      // Invalidate relevant queries
      if (profile?.id) {
        // FIXED: Invalidate all mySwapRequests queries (with and without filters)
        queryClient.invalidateQueries({ 
          queryKey: [...scheduleKeys.all, 'mySwapRequests', profile.id] 
        });
        
        // FIXED: Invalidate all swapOffersForMe queries (with and without filters)
        queryClient.invalidateQueries({ 
          queryKey: [...scheduleKeys.all, 'swapOffersForMe', profile.id] 
        });
        
        // Invalidate shifts queries if currentWeek is provided
        if (currentWeek) {
          const weekStartISO = startOfWeek(currentWeek).toISOString();
          const weekEndISO = endOfWeek(currentWeek).toISOString();
          queryClient.invalidateQueries({ 
            queryKey: scheduleKeys.myShifts(profile.id, weekStartISO, weekEndISO) 
          });
        }
      }
    },
    
    onError: (error: any) => {
      console.error('[Frontend] Error approving swap:', error);
      const errorMessage = mapSwapRpcError(error.message);
      toast.error(errorMessage);
    }
  });
};

// Export the error mapper for use in other components
export { mapSwapRpcError };
